import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/**
 * Os gráficos, sempre com a tabela ao lado (ver `Painel` em `componentes.tsx`).
 *
 * Um gráfico é resumo, não evidência: quem leva o número para uma reunião
 * precisa do valor exato, e ler valor exato de uma barra é chute. Por isso
 * nenhum gráfico aparece sozinho nesta feature — a decisão está no `Painel`, e
 * estes componentes existem só para desenhar.
 *
 * As cores saem dos tokens do design system em vez de constantes hexadecimais:
 * é o que faz o gráfico acompanhar o tema claro e o escuro sem uma segunda
 * paleta para manter em sincronia.
 */

const COR = {
  primaria: 'hsl(var(--primary))',
  sucesso: 'hsl(var(--success))',
  aviso: 'hsl(var(--warning))',
  perigo: 'hsl(var(--destructive))',
  tenue: 'hsl(var(--muted-foreground))',
  borda: 'hsl(var(--border))',
} as const;

/** Sequência para quebras por dimensão, quando não há semântica de cor. */
export const PALETA = [
  'hsl(var(--primary))',
  '#C8D93A',
  'hsl(var(--warning))',
  'hsl(var(--success))',
  'hsl(var(--destructive))',
  'hsl(var(--muted-foreground))',
] as const;

const EIXO = { stroke: COR.tenue, fontSize: 12 } as const;

const TOOLTIP = {
  contentStyle: {
    background: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '0.5rem',
    fontSize: '0.8125rem',
    color: 'hsl(var(--foreground))',
  },
  labelStyle: { color: 'hsl(var(--muted-foreground))' },
} as const;

const ALTURA = 'h-64';

/**
 * Sem animação de entrada — em todos os gráficos.
 *
 * Não é preferência estética. A revelação do Recharts é feita com
 * `requestAnimationFrame`, que o navegador congela em aba de segundo plano:
 * quem abre o relatório com ctrl+clique e volta depois encontra a linha
 * desenhada pela metade, parada, sem nenhum erro no console. Num gráfico
 * decorativo isso é feio; num relatório é um número errado na tela.
 */
const SEM_ANIMACAO = { isAnimationActive: false } as const;

/**
 * `aria-hidden` no desenho.
 *
 * O leitor de tela recebe a **tabela** ao lado, que carrega o mesmo dado em
 * forma navegável. Anunciar o SVG também faria a mesma informação ser lida
 * duas vezes, a segunda como uma lista de coordenadas.
 */
function Moldura({ children }: { children: React.ReactElement }) {
  return (
    <div className={ALTURA} aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

export interface PontoDeSerie {
  rotulo: string;
  abertos: number;
  encerrados: number;
}

export function GraficoDeSerie({ dados }: { dados: readonly PontoDeSerie[] }) {
  return (
    <Moldura>
      <LineChart data={[...dados]} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <CartesianGrid stroke={COR.borda} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="rotulo" tick={EIXO} tickLine={false} axisLine={{ stroke: COR.borda }} />
        <YAxis tick={EIXO} tickLine={false} axisLine={false} allowDecimals={false} />
        <Tooltip {...TOOLTIP} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        <Line
          type="monotone"
          dataKey="abertos"
          name="Abertos"
          stroke={COR.primaria}
          strokeWidth={2}
          dot={false}
          {...SEM_ANIMACAO}
        />
        <Line
          type="monotone"
          dataKey="encerrados"
          name="Encerrados"
          stroke={COR.sucesso}
          strokeWidth={2}
          dot={false}
          {...SEM_ANIMACAO}
        />
      </LineChart>
    </Moldura>
  );
}

export interface BarraSimples {
  rotulo: string;
  total: number;
}

export function GraficoDeBarras({
  dados,
  nome = 'Chamados',
}: {
  dados: readonly BarraSimples[];
  nome?: string;
}) {
  return (
    <Moldura>
      <BarChart
        data={[...dados]}
        layout="vertical"
        margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
      >
        <CartesianGrid stroke={COR.borda} strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={EIXO} tickLine={false} axisLine={false} allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="rotulo"
          tick={EIXO}
          tickLine={false}
          axisLine={{ stroke: COR.borda }}
          width={120}
        />
        <Tooltip {...TOOLTIP} cursor={{ fill: 'hsl(var(--accent))' }} />
        <Bar dataKey="total" name={nome} radius={[0, 4, 4, 0]} {...SEM_ANIMACAO}>
          {dados.map((linha, indice) => (
            <Cell key={linha.rotulo} fill={PALETA[indice % PALETA.length]} />
          ))}
        </Bar>
      </BarChart>
    </Moldura>
  );
}

export interface BarraDeAns {
  rotulo: string;
  cumprido: number;
  emRisco: number;
  violado: number;
}

/**
 * Empilhado de propósito: o que interessa é a **composição** de cada equipe, e
 * três barras lado a lado obrigariam a somar de cabeça para saber o total.
 */
export function GraficoDeAns({ dados }: { dados: readonly BarraDeAns[] }) {
  return (
    <Moldura>
      <BarChart
        data={[...dados]}
        layout="vertical"
        margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
      >
        <CartesianGrid stroke={COR.borda} strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tick={EIXO} tickLine={false} axisLine={false} allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="rotulo"
          tick={EIXO}
          tickLine={false}
          axisLine={{ stroke: COR.borda }}
          width={120}
        />
        <Tooltip {...TOOLTIP} cursor={{ fill: 'hsl(var(--accent))' }} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        <Bar dataKey="cumprido" name="Cumprido" stackId="ans" fill={COR.sucesso} {...SEM_ANIMACAO} />
        <Bar dataKey="emRisco" name="Em risco" stackId="ans" fill={COR.aviso} {...SEM_ANIMACAO} />
        <Bar dataKey="violado" name="Violado" stackId="ans" fill={COR.perigo} {...SEM_ANIMACAO} />
      </BarChart>
    </Moldura>
  );
}

export function GraficoDeRosca({ dados }: { dados: readonly BarraSimples[] }) {
  return (
    <Moldura>
      <PieChart>
        <Tooltip {...TOOLTIP} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        <Pie
          data={[...dados]}
          dataKey="total"
          nameKey="rotulo"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          stroke="hsl(var(--card))"
          {...SEM_ANIMACAO}
        >
          {dados.map((linha, indice) => (
            <Cell key={linha.rotulo} fill={PALETA[indice % PALETA.length]} />
          ))}
        </Pie>
      </PieChart>
    </Moldura>
  );
}

export interface BarraDeTempo {
  rotulo: string;
  mediana: number;
  p90: number;
}

/**
 * Mediana e p90 juntos, e a média fora do gráfico.
 *
 * Tempo de atendimento tem cauda longa: um chamado esquecido por três semanas
 * puxa a média e faz a equipe parecer pior do que é. A mediana diz como é o
 * dia normal; o p90 diz quão ruim fica o dia ruim. A média fica na tabela, com
 * as outras duas ao lado para quem quiser comparar.
 */
export function GraficoDeTempos({ dados }: { dados: readonly BarraDeTempo[] }) {
  return (
    <Moldura>
      {/* Sem margem negativa à esquerda: o eixo é em minutos e "1000 min" não
          cabe no recuo que serve para eixos de contagem. */}
      <BarChart data={[...dados]} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
        <CartesianGrid stroke={COR.borda} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="rotulo" tick={EIXO} tickLine={false} axisLine={{ stroke: COR.borda }} />
        <YAxis tick={EIXO} tickLine={false} axisLine={false} unit=" min" width={84} />
        <Tooltip {...TOOLTIP} cursor={{ fill: 'hsl(var(--accent))' }} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        <Bar dataKey="mediana" name="Mediana" fill={COR.primaria} radius={[4, 4, 0, 0]} {...SEM_ANIMACAO} />
        <Bar dataKey="p90" name="90º percentil" fill={COR.aviso} radius={[4, 4, 0, 0]} {...SEM_ANIMACAO} />
      </BarChart>
    </Moldura>
  );
}
